import { makeAutoObservable, reaction } from 'mobx';
import { store } from '../stores/store';
import { KeyValue } from '../typegen/key-value';
import logger from '../helpers/logger';
import type { FileInfo } from '../types/KioFileIO';
import '../types/KioFileIO';
import { isKioAppAvailable } from '../helpers/kioApp';

export class UIStore {
	isLoading = false;
	title = '';
	activeApiCalls = 0;
	private _isBackEnabled = true;
	private _isFooterEnabled = true;
	private _receiptMounted = false;
	private _showModal = false;
	private _showCart = false;
	private _loaderActive = false;
	private _customBackAction: (() => void) | null = null;
	private _screenLogoPath: string = '';
	private _printedLogoPath: string = '';
	private _isAgreedToTerms: boolean = false;
	private _pendingEarlyBarcodeScan: string | null = null;
	private _canCancel: boolean = true;
	private _balancesViewState: number = 0; // 0 = Edit view
	private _isNavCollapsed: boolean = false;
	private _overrideConfirmView: boolean = false;
	private _uiStateStack: Array<{
		title: string;
		isBackEnabled: boolean;
		canCancel: boolean;
		isNavCollapsed: boolean;
	}> = [];

	constructor() {
		makeAutoObservable(this);

		reaction(
			() => this._loaderActive,
			(isActive) => {
				logger.logGeneral.log({}, isActive ? 'Loader shown' : 'Loader hidden');
			}
		);
	}

	isBusy = () => {
		this.isLoading = true;
		store.sessionStore.touch();
	};

	isIdle = () => {
		this.isLoading = false;
		store.sessionStore.touch();
	};

	get canCancel() {
		return this._canCancel;
	}

	get isNavCollapsed() {
		return this._isNavCollapsed;
	}

	get isBackEnabled() {
		return this._isBackEnabled;
	}

	get showCart() {
		return this._showCart;
	}

	get showModal() {
		return this._showModal;
	}

	get isLoaderActive() {
		return this._loaderActive;
	}

	get screenLogoPath() {
		return this._screenLogoPath;
	}

	get printedLogoPath() {
		return this._printedLogoPath;
	}

	get isAgreedToTerms() {
		return this._isAgreedToTerms;
	}

	get balancesViewState() {
		return this._balancesViewState;
	}

	get overrideConfirmView() {
		return this._overrideConfirmView;
	}

	setNavCollapsed = (collapsed: boolean) => {
		this._isNavCollapsed = collapsed;
	};

	setCanCancel = (canCancel: boolean) => {
		this._canCancel = canCancel;
	};

	/**
	 * Snapshot current UI state and optionally apply overrides.
	 * Use with `popUIState()` to restore previous UI state on unmount.
	 */
	pushUIState = (
		overrides?: Partial<{
			title: string;
			isBackEnabled: boolean;
			canCancel: boolean;
			isNavCollapsed: boolean;
		}>
	) => {
		// Snapshot current state
		this._uiStateStack.push({
			title: this.title,
			isBackEnabled: this._isBackEnabled,
			canCancel: this._canCancel,
			isNavCollapsed: this._isNavCollapsed,
		});

		// Apply overrides if provided
		if (overrides) {
			if (overrides.title !== undefined) this.setTitle(overrides.title);
			if (overrides.isBackEnabled !== undefined) this.setBackEnabled(overrides.isBackEnabled);
			if (overrides.canCancel !== undefined) this.setCanCancel(overrides.canCancel);
			if (overrides.isNavCollapsed !== undefined) this.setNavCollapsed(overrides.isNavCollapsed);
		}
	};

	/**
	 * Pop the last snapshot and restore the UI state.
	 */
	popUIState = () => {
		const snap = this._uiStateStack.pop();
		if (!snap) return;
		this.setTitle(snap.title);
		this.setBackEnabled(snap.isBackEnabled);
		this.setCanCancel(snap.canCancel);
		this.setNavCollapsed(snap.isNavCollapsed);
	};

	setBalancesViewState = (state: number) => {
		this._balancesViewState = state;
	};

	setOverrideConfirmView = (override: boolean) => {
		this._overrideConfirmView = override;
	};

	showLoader = () => {
		this.setShowModal(true);
		this._loaderActive = true;
		store.sessionStore.touch();
	};

	hideLoader = () => {
		this._loaderActive = false;
		this.setShowModal(false);
		store.sessionStore.touch();
	};

	get isFooterEnabled() {
		return this._isFooterEnabled;
	}

	setBackEnabled = (isEnabled: boolean) => {
		this._isBackEnabled = isEnabled;
	};

	isCustomBackActionSet = () => this._customBackAction !== null;

	setCustomBackAction = (action: () => void) => {
		this._customBackAction = action;
	};

	setAgreedToTerms = (agreed: boolean) => {
		this._isAgreedToTerms = agreed;
	};

	get pendingEarlyBarcodeScan() {
		return this._pendingEarlyBarcodeScan;
	}

	setPendingEarlyBarcodeScan = (scan: string | null) => {
		this._pendingEarlyBarcodeScan = scan;
	};

	clearCustomBackAction = () => {
		this._customBackAction = null;
	};

	setTitle = (newTitle: string) => {
		this.title = newTitle;
	};

	setFooterEnabled = (enabled: boolean) => {
		this._isFooterEnabled = enabled;
	};

	setShowModal = (show: boolean) => {
		this._showModal = show;
	};

	setShowCart = (show: boolean) => {
		this._showCart = show;
	};

	setLogoImages = () => {
		if (!isKioAppAvailable()) return;
		try {
			const root = `${KioApp.GetUserDataDir()}Logos`;
			const logoFiles: FileInfo[] = JSON.parse(KioFileIO.GetDirectoryList(root, false, 'svg,png,jpg,jpeg', 0)) as FileInfo[];
			this._screenLogoPath = this.loadLocalImage(logoFiles.find((file) => file.Name.toLowerCase().includes('screen'))?.URI) || '';
			this._printedLogoPath = this.loadLocalImage(logoFiles.find((file) => file.Name.toLowerCase().includes('printed'))?.URI) || '';
		} catch (error) {
			logger.logGeneral.error({}, `There was an error loading logo images`, `Error: ${error}`);
		}
	};

	get receiptMounted() {
		return this._receiptMounted;
	}

	setReceiptMounted = (mounted: boolean) => {
		this._receiptMounted = mounted;
	};

	handleNavigation = async (pageId: string, extraParams?: KeyValue[] | null, isSessionEnd = false) => {
		if (pageId === 'end-session') {
			await store.sessionStore.startEndSessionTasks();
			return;
		}

		if (this._pendingEarlyBarcodeScan && pageId === 'find-your-account') {
			const hasTCPage = store.configStore.config?.pages?.some((page) => page.id === 'terms-and-conditions');
			if (!hasTCPage) {
				logger.logGeneral.error(
					{},
					`Early barcode scan redirect cannot run: 'terms-and-conditions' page not found in config. Ensure the page exists to prevent customers from bypassing T&C.`,
					'UIStore.handleNavigation'
				);
				this.setPendingEarlyBarcodeScan(null);
			} else {
				const lookupAccountPage = store.configStore.config?.pages?.find((page) => page.id === 'lookup-account');
				const lookupFormComponent = lookupAccountPage?.components?.find((c) => c.type === 3);
				const barcodeOption = lookupFormComponent?.data?.lookupOptions?.find(
					(opt: { lookupType: number }) => opt.lookupType === 1 || opt.lookupType === 5
				);
				if (barcodeOption) {
					store.accountStore.setBarcodeData(this._pendingEarlyBarcodeScan);
					this.setPendingEarlyBarcodeScan(null);
					pageId = 'lookup-account';
					extraParams = [{ key: 'lookupType', value: String(barcodeOption.lookupType) }];
				} else {
					logger.logGeneral.error(
						{},
						`Early barcode scan redirect cannot run: no barcode lookup option found in 'lookup-account' page config.`,
						'UIStore.handleNavigation'
					);
					this.setPendingEarlyBarcodeScan(null);
				}
			}
		}

		if (store.kioskStore.useGlobalBarcodeScanner) {
			if (pageId === 'terms-and-conditions') this.setAgreedToTerms(false);
			if (pageId === 'find-your-account') this.setAgreedToTerms(true);
		}

		const newParams = extraParams && extraParams.length > 0 ? extraParams : [];
		store.configStore.setPageParams(newParams);
		if (pageId === 'escrow-notice' && !store.accountStore.showEscrowNotice)
			pageId = store.configStore?.config?.pages?.find((page) => page.id === 'escrow-notice')?.nextPageId || 'payment-method';
		logger.logNavigation.log(
			{
				destinationId: pageId,
			},
			`Navigating to page: '${pageId}' with params: ${JSON.stringify(extraParams)}`
		);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).KioUtils) window.KioUtils.LogPageStats(`${window.location.href.replace(/\/$/, '')}/${pageId}`, pageId);
		store.configStore.setCurrentPageId(pageId);
		if (isSessionEnd) await store.sessionStore.startEndSessionTasks();
		else store.sessionStore.legacyPostNavUrl(pageId);
	};

	loadLocalImage = (filePath?: string): string | null => {
		if (filePath) {
			try {
				// First, get the file details to determine the file size
				const fileDetailsJson = KioFileIO.GetFileDetails(filePath);
				const fileDetails: FileInfo = JSON.parse(fileDetailsJson);

				// Check if file exists
				if (!fileDetails.Exists || !fileDetails.IsFile) {
					console.error('File does not exist or is not a file:', filePath);
					return null;
				}

				// Read the entire file using the actual file size
				const base64Data = KioFileIO.ReadFileDataBase64(filePath, 0, fileDetails.Length);

				const fileExtension = filePath.split('.').pop()?.toLowerCase();
				let mimeType: string;
				switch (fileExtension) {
					case 'png':
						mimeType = 'image/png';
						break;
					case 'jpg':
					case 'jpeg':
						mimeType = 'image/jpeg';
						break;
					case 'svg':
						mimeType = 'image/svg+xml';
						break;
					default:
						mimeType = 'image/png';
						break;
				}

				return `data:${mimeType};base64,${base64Data}`;
			} catch (error) {
				console.error('Failed to load local image:', error);
			}
		}
		return null;
	};

	handleCustomBackAction = () => this._customBackAction?.();
}
